import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { asId, type ConversationId, type Uuid } from '@cyphex/types';
import { chatApi } from '@/features/chat/api';
import { newUuid } from '@/lib/ids';
/* Deep imports: the notifications barrel would pull the settings forms into
   whatever chunk this hook lands in. */
import { notificationActions } from '@/features/notifications/notificationStore';
import { notificationApi } from '@/features/notifications/notificationsClient';
import { notify } from '@/features/notifications/toastApi';
import { useDesktopEvent } from './useDesktopEvent';

/**
 * Anything the OS can ask the app to go and show: a clicked banner, a tray or
 * menu item, and `cyphex-teams://` links — including the one that cold-started
 * the process, which the main side buffers until this listener exists.
 *
 * Also carries the macOS inline reply, because the banner it comes from is the
 * same object the click came from.
 */
export function useDesktopNavigation(): void {
  const navigate = useNavigate();

  const open = useCallback(
    (path: string) => {
      if (!path.startsWith('/')) return;
      navigate(path);
    },
    [navigate],
  );

  useDesktopEvent('deep-link', (event) => open(event.path));
  useDesktopEvent('navigate', (event) => open(event.path));

  useDesktopEvent('notification-activated', (event) => {
    notificationActions.markRead(event.id);
    void notificationApi.markRead(event.id).catch(() => undefined);
    if (event.deepLink !== undefined) open(event.deepLink);
  });

  useDesktopEvent('notification-reply', (event) => {
    const conversationId = event.conversationId;
    const text = event.text.trim();
    if (conversationId === undefined || text.length === 0) return;

    notificationActions.markRead(event.id);
    void chatApi
      .sendMessage(asId<ConversationId>(conversationId), {
        clientMsgId: asId<Uuid>(newUuid()),
        text,
      })
      .then(() => notify.success('Reply sent.'))
      /* The banner is already gone, so a silent failure would be invisible.
         Surface it in-app and leave the text in the toast. */
      .catch(() => notify.error('Your reply could not be sent.'));
  });
}
